import { Suspense, lazy, useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { canSee, homeFor } from "@/lib/roles";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import { ForgotPassword, ResetPassword } from "@/pages/PasswordReset";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Piano from "@/pages/Piano";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentResult";

const Venditori = lazy(() => import("@/pages/Venditori"));
const VenditoreDetail = lazy(() => import("@/pages/VenditoreDetail"));
const LeadExplorer = lazy(() => import("@/pages/LeadExplorer"));
const Hermes = lazy(() => import("@/pages/Hermes"));
const Processo = lazy(() => import("@/pages/Processo"));
const Attivita = lazy(() => import("@/pages/Attivita"));
const MetaAds = lazy(() => import("@/pages/MetaAds"));
const ClientiAgenzia = lazy(() => import("@/pages/ClientiAgenzia"));
const GoogleAds = lazy(() => import("@/pages/GoogleAds"));
const Onboarding = lazy(() => import("@/pages/Onboarding"));
const Assistenza = lazy(() => import("@/pages/Assistenza"));
const WhatsApp = lazy(() => import("@/pages/WhatsApp"));
const Persone = lazy(() => import("@/pages/Persone"));
const Avvisi = lazy(() => import("@/pages/Avvisi"));
const ClickUp = lazy(() => import("@/pages/ClickUp"));
const PasswordPage = lazy(() => import("@/pages/PasswordPage"));
const Registro = lazy(() => import("@/pages/Registro"));
const Impostazioni = lazy(() => import("@/pages/Impostazioni"));
const Admin = lazy(() => import("@/pages/Admin"));
const Console = lazy(() => import("@/pages/Console"));
const Salute = lazy(() => import("@/pages/Salute"));
const Calendario = lazy(() => import("@/pages/Calendario"));
const Drive = lazy(() => import("@/pages/Drive"));
const Costi = lazy(() => import("@/pages/Costi"));
import RequireModule from "@/components/RequireModule";

import { PageSkeleton } from "@/components/Skeleton";
const PageFallback = () => <PageSkeleton cards={2} />;
import LoadingScreen from "@/components/LoadingScreen";
import ShellSkeleton from "@/components/ShellSkeleton";

const APP_START = Date.now();
// Dopo il login: animazione video 4s. Al refresh/primo caricamento: solo logo 2s.
const SPLASH_VIDEO = sessionStorage.getItem("athanor_splash") === "video";
sessionStorage.removeItem("athanor_splash");
const MIN_SPLASH_MS = SPLASH_VIDEO ? 4000 : 0;
let splashDone = false;

function useSplash() {
  const [ready, setReady] = useState(splashDone);
  useEffect(() => {
    if (splashDone) return;
    const t = setTimeout(() => { splashDone = true; setReady(true); }, Math.max(0, MIN_SPLASH_MS - (Date.now() - APP_START)));
    return () => clearTimeout(t);
  }, []);
  return ready;
}

function Splash() {
  return <LoadingScreen video={SPLASH_VIDEO} />;
}

function Protected() {
  const { user } = useAuth();
  const location = useLocation();
  const splashReady = useSplash();
  if (!splashReady) return <Splash />;
  if (location.state?.user) return <Layout />;
  if (user === null) return <ShellSkeleton />;
  if (user === false) return <Navigate to="/login" replace />;
  if (user?.billing?.blocked && !user?.impersonated_by) return <Navigate to="/piano" replace />;
  return <Layout />;
}

function BlockedOnly() {
  const { user } = useAuth();
  if (user === null) return <ShellSkeleton />;
  if (user === false) return <Navigate to="/login" replace />;
  if (!user?.billing?.blocked) return <Navigate to="/" replace />;
  return <Piano />;
}

function HomeRoute() {
  const { user } = useAuth();
  if (user?.role === "admin" && !user?.impersonated_by) return <Navigate to="/console" replace />;
  if (user?.team_role && !canSee(user, "/")) return <Navigate to={homeFor(user)} replace />;
  return <Dashboard />;
}

function RoleGate() {
  const { user } = useAuth();
  const location = useLocation();
  if (user?.team_role && location.pathname !== "/" && !canSee(user, location.pathname) && location.pathname !== "/password") {
    return <Navigate to={homeFor(user)} replace />;
  }
  return <Outlet />;
}

function ProtectedBare({ children }) {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  const splashReady = useSplash();
  if (!splashReady || user === null) return <Splash />;
  if (user) return <Navigate to="/" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Il callback OAuth va intercettato in modo sincrono durante il render (non in useEffect)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Suspense fallback={<PageFallback />}>
    <Routes>
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
      <Route path="/reset-password" element={<PublicOnly><ResetPassword /></PublicOnly>} />
      <Route path="/onboarding" element={<ProtectedBare><Onboarding /></ProtectedBare>} />
      <Route path="/piano" element={<BlockedOnly />} />
      <Route path="/payment/success" element={<ProtectedBare><PaymentSuccess /></ProtectedBare>} />
      <Route path="/payment/cancel" element={<ProtectedBare><PaymentCancel /></ProtectedBare>} />
      <Route element={<Protected />}>
      <Route element={<RoleGate />}>
        <Route path="/" element={<HomeRoute />} />
        <Route path="/venditori" element={<Venditori />} />
        <Route path="/venditori/:id" element={<VenditoreDetail />} />
        <Route path="/lead-explorer" element={<LeadExplorer />} />
        <Route path="/hermes" element={<Hermes />} />
        <Route path="/processo" element={<Processo />} />
        <Route path="/attivita" element={<Attivita />} />
        <Route path="/meta-ads" element={<MetaAds />} />
        <Route path="/clienti" element={<RequireModule feature="La vista clienti agenzia"><ClientiAgenzia /></RequireModule>} />
        <Route path="/google-ads" element={<RequireModule feature="La pagina Google Ads"><GoogleAds /></RequireModule>} />
        <Route path="/assistenza" element={<Assistenza />} />
        <Route path="/whatsapp" element={<WhatsApp />} />
        <Route path="/whatsapp/registro" element={<Registro />} />
        <Route path="/persone" element={<Persone />} />
        <Route path="/calendario" element={<Calendario />} />
        <Route path="/drive" element={<Drive />} />
        <Route path="/costi" element={<Costi />} />
        <Route path="/avvisi" element={<Avvisi />} />
        <Route path="/clickup" element={<RequireModule feature="ClickUp"><ClickUp /></RequireModule>} />
        <Route path="/impostazioni" element={<Impostazioni />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/console" element={<Console />} />
        <Route path="/console/salute" element={<Salute />} />
        <Route path="/password" element={<PasswordPage />} />
      </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <div className="App dark">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster theme="dark" position="bottom-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
